import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import DOMPurify from "dompurify";
import { ArrowLeft, Sparkle, CalendarBlank, Buildings, CurrencyDollar, Briefcase, FileText, FilePdf, Robot, Wallet, Warning, Plus, Trash, Scales, ChartBar, MagnifyingGlass, CheckSquare, Square, ArrowsClockwise, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import SignatarioDownloadModal from "@/components/SignatarioDownloadModal";
import SignatarioPicker from "@/components/SignatarioPicker";
import ContractEditModal, { canEditContract } from "@/components/ContractEditModal";
import { useAuth } from "@/context/AuthContext";
import MedicoesContratoPanel from "@/components/medicao/MedicoesContratoPanel";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

function MarkdownLite({ text }) {
  // Very small markdown rendering: bold + lists + headings.
  const rawHtml = (text || "")
    .replace(/^### (.*$)/gm, '<h3 class="font-heading text-lg mt-5 mb-2 text-[#F59E0B]">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="font-heading text-xl mt-6 mb-3 text-[#F59E0B]">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="font-heading text-2xl mt-6 mb-3 gold-text">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#FCD34D]">$1</strong>')
    .replace(/^\- (.*)$/gm, '<li class="ml-5 list-disc text-[#E9EEF5]">$1</li>')
    .replace(/\n\n/g, '<br/><br/>');
  const safeHtml = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ["h1", "h2", "h3", "strong", "li", "br", "ul", "ol", "p", "em"],
    ALLOWED_ATTR: ["class"],
  });
  return <div className="prose prose-invert max-w-none text-[#E9EEF5] leading-relaxed" dangerouslySetInnerHTML={{ __html: safeHtml }} />;
}

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPesquisa, setShowPesquisa] = useState(false);
  const [saldo, setSaldo] = useState(null);
  const [showExecForm, setShowExecForm] = useState(false);
  const [execForm, setExecForm] = useState({ valor: "", tipo: "empenho", descricao: "", numero_nota_fiscal: "", data_emissao_nf: "", signatario_id: "" });
  const [savingExec, setSavingExec] = useState(false);
  const [execItens, setExecItens] = useState([]);  // itens debitados na NF: [{numero_item, quantidade, valor_unitario}]
  const [aditivos, setAditivos] = useState([]);
  const [pesquisas, setPesquisas] = useState([]);
  const [pesquisaItemContext, setPesquisaItemContext] = useState(null);  // item do contrato para pré-preencher pesquisa

  const load = useCallback(() => api.get(`/contracts/${id}`).then((r) => setContract(r.data)), [id]);
  const loadSaldo = useCallback(() => api.get(`/contracts/${id}/execucoes`).then((r) => setSaldo(r.data)), [id]);
  const loadAditivos = useCallback(() => api.get(`/contracts/${id}/aditivos`).then((r) => setAditivos(r.data || [])), [id]);
  const loadPesquisas = useCallback(() => api.get(`/contracts/${id}/pesquisas-precos`).then((r) => setPesquisas(r.data?.items || [])), [id]);
  useEffect(() => { load(); loadSaldo(); loadAditivos(); loadPesquisas(); }, [load, loadSaldo, loadAditivos, loadPesquisas]);

  const addExecucao = async (e) => {
    e.preventDefault();
    const valor = parseFloat(execForm.valor);
    if (!valor || valor <= 0) { toast.error("Informe um valor maior que zero"); return; }
    if (!execForm.signatario_id) { toast.error("Selecione o assinante do documento antes de registrar"); return; }
    setSavingExec(true);
    try {
      const { data } = await api.post(`/contracts/${id}/execucoes`, {
        valor, tipo: execForm.tipo, descricao: execForm.descricao,
        numero_nota_fiscal: execForm.numero_nota_fiscal,
        data_emissao_nf: execForm.data_emissao_nf,
        itens_debitados: execItens.filter((it) => it.quantidade > 0).map((it) => ({
          numero_item: parseInt(it.numero_item, 10),
          quantidade: parseFloat(it.quantidade),
          valor_unitario: it.valor_unitario ? parseFloat(it.valor_unitario) : undefined,
        })),
      });
      toast.success("Execução registrada");
      const nfNumero = execForm.numero_nota_fiscal;
      const sigId = execForm.signatario_id;
      setExecForm({ valor: "", tipo: "empenho", descricao: "", numero_nota_fiscal: "", data_emissao_nf: "", signatario_id: "" });
      setExecItens([]);
      setShowExecForm(false);
      loadSaldo();
      load();  // recarrega contrato para atualizar quantidade_executada nos itens
      // Geração automática do relatório PDF da nota fiscal recém-cadastrada
      if (data?.exec_id) {
        try {
          const res = await fetch(`${API}/contracts/${id}/execucoes/${data.exec_id}/report?signatario_id=${encodeURIComponent(sigId)}`, { credentials: "include" });
          if (res.ok) {
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const safeNf = (nfNumero || data.exec_id.slice(0, 8)).replace(/[^\w.-]/g, "_");
            a.href = url; a.download = `relatorio_NF_${safeNf}.pdf`; a.click();
            window.URL.revokeObjectURL(url);
            toast.success(`Relatório da NF ${nfNumero || data.exec_id.slice(0, 8)} gerado automaticamente`);
          }
        } catch { /* falha silenciosa — usuário ainda pode baixar do histórico */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar");
    } finally { setSavingExec(false); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/contracts/${id}/analyze`);
      setContract({ ...contract, ia_analise: data.analise, ia_analisado_em: new Date().toISOString() });
      toast.success("Análise gerada com sucesso");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro na análise";
      toast.error(typeof msg === "string" ? msg : "Erro na análise");
    } finally {
      setAnalyzing(false);
    }
  };

  const deleteContract = async () => {
    if (!window.confirm(`Excluir o contrato ${contract.numero}?\n\nEsta ação também removerá:\n• Todos os aditivos vinculados\n• Todas as execuções financeiras\n• Notificações e relatórios PDF arquivados\n\nNão é possível desfazer.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/contracts/${id}`);
      toast.success(`Contrato ${contract.numero} excluído`);
      navigate("/contratos");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
      setDeleting(false);
    }
  };

  let analyzeButtonLabel;
  if (!contract) return <div className="text-[#94A3B8]">Carregando…</div>;
  if (analyzing) analyzeButtonLabel = "Analisando…";
  else if (contract.ia_analise) analyzeButtonLabel = "Gerar nova análise";
  else analyzeButtonLabel = "Gerar análise";

  return (
    <div className="space-y-8 max-w-6xl">
      <Link to="/contratos" data-testid="back-to-contracts"
            className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#F59E0B]">
        <ArrowLeft size={14} /> Voltar para contratos
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-label">Contrato Nº</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2 gold-text">{contract.numero}</h1>
          <p className="text-[#E9EEF5] mt-3 text-lg leading-relaxed">{contract.objeto}</p>
        </div>
        {canEditContract(contract, user) && (
          <button
            data-testid="edit-contract-button"
            onClick={() => setShowEdit(true)}
            title="Editar dados do contrato"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-sm font-medium transition-colors"
          >
            <PencilSimple size={14} weight="bold" />
            Editar dados do contrato
          </button>
        )}
        <button
          data-testid="delete-contract-button"
          onClick={deleteContract}
          disabled={deleting}
          title="Excluir contrato e dados vinculados"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444] text-sm font-medium transition-colors disabled:opacity-50"
        >
          <Trash size={14} weight="duotone" />
          {deleting ? "Excluindo…" : "Excluir contrato"}
        </button>
        <button
          data-testid="pesquisa-precos-button"
          onClick={() => setShowPesquisa(true)}
          title="Pesquisa de Preços (IN 65/2021)"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 hover:border-[#F59E0B] text-sm font-medium transition-colors"
        >
          <ChartBar size={14} weight="duotone" />
          Pesquisa de Preços
        </button>
      </div>

      {showEdit && (
        <ContractEditModal
          contract={contract}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setShowEdit(false); setContract(updated); loadSaldo(); }}
        />
      )}

      {/* Info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InfoCard icon={Buildings} label="Município" value={contract.municipio_nome} />
        <InfoCard icon={Briefcase} label="Órgão" value={contract.orgao} />
        <InfoCard icon={FileText} label="Modalidade" value={contract.modalidade} />
        <InfoCard icon={CurrencyDollar} label="Valor" value={fmtBRL(contract.valor)} accent />
        <InfoCard icon={CalendarBlank} label="Vigência" value={`${contract.data_inicio?.slice(0,10)} a ${contract.data_fim?.slice(0,10)}`} />
        <InfoCard label="Fornecedor" value={`${contract.fornecedor}${contract.cnpj_fornecedor ? ` · ${contract.cnpj_fornecedor}` : ""}`} />
        {contract.fiscal_responsavel && (
          <InfoCard icon={Buildings} label="Fiscal Responsável (TCE)" value={contract.fiscal_responsavel} />
        )}
      </div>

      <MedicoesContratoPanel contractId={id} />

      {/* Painel de Saldo */}
      <SaldoPanel
        saldo={saldo}
        contractId={id}
        contractMunicipioId={contract?.municipio_id}
        contractItens={contract?.itens || []}
        showForm={showExecForm}
        toggleForm={() => setShowExecForm((s) => !s)}
        execForm={execForm}
        setExecForm={setExecForm}
        execItens={execItens}
        setExecItens={setExecItens}
        onSubmit={addExecucao}
        saving={savingExec}
        onExecDeleted={() => { loadSaldo(); load(); }}
      />

      {/* Itens do contrato + pesquisa item-a-item */}
      <ContractItensPanel
        contract={contract}
        pesquisas={pesquisas}
        onOpenPesquisa={(item) => { setPesquisaItemContext(item); setShowPesquisa(true); }}
      />

      {/* Aditivos sem pesquisa de preços vinculada (IN 65/2021) */}
      <AditivosPendentesAlert
        aditivos={aditivos}
        pesquisas={pesquisas}
        onOpenPesquisa={() => setShowPesquisa(true)}
        onLinked={() => { loadAditivos(); loadPesquisas(); }}
      />

      {/* AI Analysis */}
      <div className="tce-card p-8 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#F59E0B]/10 blur-3xl pointer-events-none" />
        <div className="flex items-center justify-between mb-6 relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B]">
              <Robot size={22} weight="duotone" />
            </div>
            <div>
              <div className="uppercase-label">Auditoria Assistida</div>
              <div className="font-heading text-2xl mt-1">Análise por IA</div>
            </div>
          </div>
          <button
            data-testid="analyze-contract-button"
            onClick={analyze}
            disabled={analyzing}
            className="px-5 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] font-medium hover:bg-[#FCD34D] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Sparkle size={16} weight={analyzing ? "regular" : "fill"} />
            {analyzeButtonLabel}
          </button>
        </div>

        {contract.ia_analise ? (
          <div className="relative">
            <div className="text-xs text-[#94A3B8] mb-4">
              Última análise: {contract.ia_analisado_em ? new Date(contract.ia_analisado_em).toLocaleString("pt-BR") : "—"}
            </div>
            <MarkdownLite text={contract.ia_analise} />
          </div>
        ) : (
          <div className="text-[#94A3B8] text-sm relative">
            Solicite uma análise por IA para obter resumo executivo, avaliação de risco e recomendações de fiscalização.
          </div>
        )}
      </div>

      {showPesquisa && (
        <PesquisaPrecosModal
          contractId={id}
          contract={contract}
          itemContext={pesquisaItemContext}
          onClose={() => { setShowPesquisa(false); setPesquisaItemContext(null); loadPesquisas(); load(); }}
        />
      )}
    </div>
  );
}

function ContractItensPanel({ contract, pesquisas, onOpenPesquisa }) {
  const itens = contract?.itens || [];
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  if (itens.length === 0) return null;

  // Mapa item->pesquisa mais recente
  const pesqByItem = {};
  (pesquisas || []).forEach((p) => {
    if (p.contract_item_numero) {
      const cur = pesqByItem[p.contract_item_numero];
      if (!cur || new Date(p.created_at) > new Date(cur.created_at)) {
        pesqByItem[p.contract_item_numero] = p;
      }
    }
  });

  const totalItens = itens.reduce((s, it) => s + (it.valor_total || 0), 0);
  const comPesquisa = Object.keys(pesqByItem).length;

  const baixarConsolidado = () => setShowDownloadModal(true);

  return (
    <div data-testid="contract-itens-panel" className="tce-card p-6">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Itens do contrato · IN 65/2021</div>
          <div className="font-heading text-xl mt-1">Composição por item ({itens.length})</div>
          <p className="text-xs text-[#94A3B8] mt-1">
            Soma dos itens: <span className="font-mono text-[#FCD34D]">{fmtBRL(totalItens)}</span> ·
            {" "}{comPesquisa}/{itens.length} com pesquisa de preços vinculada
          </p>
        </div>
        <button
          data-testid="pdf-consolidado-button"
          onClick={baixarConsolidado}
          disabled={comPesquisa === 0}
          title={comPesquisa === 0 ? "Realize ao menos uma pesquisa de preços antes" : "Gerar PDF consolidado por contrato"}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
        >
          <FilePdf size={16} weight="duotone" />
          Gerar PDF Consolidado da Pesquisa
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase text-[#94A3B8]">
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">Descrição</th>
              <th className="px-2 py-2 text-right">Qtd</th>
              <th className="px-2 py-2 text-left">Unid</th>
              <th className="px-2 py-2 text-right">Vl. Unit.</th>
              <th className="px-2 py-2 text-right">Total</th>
              <th className="px-2 py-2 text-right">Preço-ref. pesquisado</th>
              <th className="px-2 py-2 text-center">Ação</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((it) => {
              const p = pesqByItem[it.numero_item];
              const ref = p?.estatisticas?.preco_referencia || 0;
              const diff = ref > 0 && it.valor_unitario > 0
                ? ((it.valor_unitario - ref) / ref) * 100 : null;
              return (
                <tr key={it.numero_item}
                    data-testid={`item-contrato-${it.numero_item}`}
                    className="border-t border-[#334155]">
                  <td className="px-2 py-2 font-mono text-[#94A3B8]">{it.numero_item}</td>
                  <td className="px-2 py-2 text-[#E9EEF5]">{it.descricao}</td>
                  <td className="px-2 py-2 text-right font-mono">{(it.quantidade || 0).toLocaleString("pt-BR")}</td>
                  <td className="px-2 py-2 text-[#94A3B8]">{it.unidade_medida}</td>
                  <td className="px-2 py-2 text-right font-mono text-[#FCD34D]">{fmtBRL(it.valor_unitario)}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtBRL(it.valor_total)}</td>
                  <td className="px-2 py-2 text-right font-mono">
                    {p ? (
                      <div>
                        <div className="text-[#F59E0B]">{fmtBRL(ref)}</div>
                        {diff !== null && (
                          <div className={`text-[10px] ${Math.abs(diff) <= 10 ? "text-[#10B981]" : Math.abs(diff) <= 25 ? "text-[#F59E0B]" : "text-[#EF4444]"}`}>
                            {diff > 0 ? "+" : ""}{diff.toFixed(1)}% vs cadastrado
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[#94A3B8] text-[10px] italic">sem pesquisa</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button
                      data-testid={`pesquisar-item-${it.numero_item}`}
                      onClick={() => onOpenPesquisa(it)}
                      className={`px-2.5 py-1 rounded text-[10px] font-medium ${p ? "border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10" : "bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D]"}`}
                    >
                      {p ? "🔄 Re-pesquisar" : "🔍 Pesquisar preço"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showDownloadModal && (
        <SignatarioDownloadModal
          municipioId={contract.municipio_id}
          urlBase={`/contracts/${contract.id}/pesquisa-precos-consolidado`}
          filename={`pesquisa_consolidada_${(contract.numero || "contrato").replace(/[^A-Za-z0-9_.-]/g, "_")}.pdf`}
          title="PDF Consolidado da Pesquisa"
          subtitle={`Contrato ${contract.numero || ""}`}
          onClose={() => setShowDownloadModal(false)}
        />
      )}
    </div>
  );
}

function AditivosPendentesAlert({ aditivos, pesquisas, onOpenPesquisa, onLinked }) {
  const [linking, setLinking] = useState(null);
  const [selecoes, setSelecoes] = useState({});

  // Aditivos de valor/misto sem vinculação à pesquisa de preços
  const pendentes = (aditivos || []).filter(
    (a) => (a.tipo === "valor" || a.tipo === "misto") && !a.pesquisa_precos_id
  );
  if (pendentes.length === 0) return null;

  const vincular = async (aditivoId) => {
    const pesqId = selecoes[aditivoId];
    if (!pesqId) { toast.error("Selecione uma pesquisa"); return; }
    setLinking(aditivoId);
    try {
      await api.patch(`/aditivos/${aditivoId}`, { pesquisa_precos_id: pesqId });
      toast.success("Pesquisa vinculada ao aditivo");
      onLinked?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao vincular");
    } finally {
      setLinking(null);
    }
  };

  return (
    <div data-testid="aditivos-pendentes-alert" className="tce-card p-6 border-[#F59E0B]/30">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B] shrink-0">
            <Warning size={20} weight="duotone" />
          </div>
          <div>
            <div className="uppercase-label !text-[#F59E0B]">Compliance IN 65/2021</div>
            <div className="font-heading text-xl mt-1">
              {pendentes.length} aditivo{pendentes.length > 1 ? "s" : ""} de valor sem pesquisa de preços
            </div>
            <p className="text-xs text-[#94A3B8] mt-1 max-w-2xl">
              Aditivos de acréscimo/supressão de valor exigem pesquisa formal de preços conforme
              IN SEGES/ME nº 65/2021. Vincule uma pesquisa existente ou crie uma nova.
            </p>
          </div>
        </div>
        <button
          data-testid="criar-pesquisa-rapida"
          onClick={onOpenPesquisa}
          className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 shrink-0"
        >
          <Plus size={14} weight="bold" /> Criar nova pesquisa
        </button>
      </div>

      <div className="space-y-2">
        {pendentes.map((a) => (
          <div
            key={a.id}
            data-testid={`aditivo-pendente-${a.id}`}
            className="rounded-md border border-[#F59E0B]/20 bg-[#F59E0B]/5 p-3"
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="text-sm">
                <span className="font-mono text-[#F59E0B]">{a.numero}</span>
                <span className="text-xs text-[#94A3B8] ml-2">· {a.tipo}</span>
                {a.valor_adicional ? (
                  <span className={`ml-2 font-mono text-xs ${a.valor_adicional > 0 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>
                    {fmtBRL(a.valor_adicional)}
                  </span>
                ) : null}
              </div>
              <div className="text-[10px] text-[#94A3B8]">
                {new Date(a.created_at).toLocaleDateString("pt-BR")} · {a.fiscal_name}
              </div>
            </div>
            <div className="text-xs text-[#94A3B8] mb-2 line-clamp-2">{a.justificativa}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid={`select-pesquisa-${a.id}`}
                value={selecoes[a.id] || ""}
                onChange={(e) => setSelecoes((s) => ({ ...s, [a.id]: e.target.value }))}
                disabled={pesquisas.length === 0}
                className="tce-input flex-1 min-w-[260px] px-3 py-2 rounded-md text-xs disabled:opacity-50"
              >
                <option value="">
                  {pesquisas.length === 0
                    ? "Nenhuma pesquisa disponível — crie uma primeiro"
                    : "Selecione uma pesquisa existente…"}
                </option>
                {pesquisas.map((p) => {
                  const data = new Date(p.created_at).toLocaleDateString("pt-BR");
                  const media = p.estatisticas?.preco_referencia || p.estatisticas?.media || 0;
                  const usada = p.aditivo_id && p.aditivo_id !== a.id;
                  return (
                    <option key={p.id} value={p.id} disabled={usada}>
                      {data} · {p.termo} · ref {fmtBRL(media)}{usada ? " (já vinculada a outro aditivo)" : ""}
                    </option>
                  );
                })}
              </select>
              <button
                data-testid={`vincular-pesquisa-${a.id}`}
                onClick={() => vincular(a.id)}
                disabled={linking === a.id || !selecoes[a.id]}
                className="px-3 py-2 rounded-md border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10 text-xs font-medium flex items-center gap-1.5 disabled:opacity-40"
              >
                <Scales size={12} weight="duotone" />
                {linking === a.id ? "Vinculando…" : "Vincular"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PesquisaPrecosModal({ contractId, contract, itemContext, onClose }) {
  const [form, setForm] = useState({
    termo: itemContext?.descricao
      ? itemContext.descricao.slice(0, 80)
      : (contract.objeto || "").split(/[,.]/)[0].slice(0, 80),
    data_inicio: "",
    data_fim: "",
    incluir_pncp: true,
    incluir_tce: true,
  });
  const [modo, setModo] = useState(itemContext ? "indexados" : "contratos"); // contratos | indexados
  const [phase, setPhase] = useState("form"); // form | preview | indexed
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());  // item-level keys
  const [expandedKeys, setExpandedKeys] = useState(new Set());   // contract keys expanded
  const [itemsCache, setItemsCache] = useState({});               // { [contractKey]: {items, semItens, motivo, loaded, loading} }
  const [indexedItems, setIndexedItems] = useState([]);            // resultado /itens-indexados
  const [selectedIndexed, setSelectedIndexed] = useState(new Set());
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/contracts/${contractId}/pesquisas-precos`)
      .then((r) => setHistory(r.data.items || []))
      .catch(() => {});
  }, [contractId]);

  const submit = async (e) => {
    e.preventDefault();
    setSearching(true);
    try {
      if (modo === "indexados") {
        const { data } = await api.get(`/pesquisa-precos/itens-indexados`, {
          params: { termo: form.termo, limite: 200 },
        });
        setIndexedItems(data.itens || []);
        // Pré-seleciona todos
        setSelectedIndexed(new Set((data.itens || []).map((_, i) => i)));
        setPhase("indexed");
        setResult(null);
        const sufLive = data.live_consultado
          ? ` (${data.fonte_local} locais + ${data.fonte_live} ao vivo no Compras.gov.br)`
          : "";
        toast.success(`${data.total} itens encontrados${sufLive}`);
      } else {
        const { data } = await api.post(`/contracts/${contractId}/pesquisa-precos/preview`, form);
        setPreview(data);
        const defaults = new Set(
          (data.candidatos || []).filter((c) => !c.is_outlier).map((c) => `${c.key}#unico`)
        );
        setSelectedKeys(defaults);
        setExpandedKeys(new Set());
        setItemsCache({});
        setPhase("preview");
        setResult(null);
        toast.success(`${data.candidatos?.length || 0} contratos encontrados — expanda para ver itens`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na pesquisa");
    } finally {
      setSearching(false);
    }
  };

  const toggleIndexed = (idx) => {
    setSelectedIndexed((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const statsLiveIndexed = (() => {
    const vs = [...selectedIndexed]
      .map((i) => indexedItems[i]?.valor_unitario || 0)
      .filter((v) => v > 0).sort((a, b) => a - b);
    if (!vs.length) return { qtd: 0, menor: 0, maior: 0, media: 0, mediana: 0, cv: 0, ref: 0 };
    const media = vs.reduce((a, b) => a + b, 0) / vs.length;
    const mediana = vs.length % 2 === 0 ? (vs[vs.length / 2 - 1] + vs[vs.length / 2]) / 2 : vs[Math.floor(vs.length / 2)];
    const desv = Math.sqrt(vs.reduce((a, b) => a + (b - media) ** 2, 0) / vs.length);
    const cv = media > 0 ? (desv / media) * 100 : 0;
    const ref = cv <= 25 ? media : vs[0];
    return { qtd: vs.length, menor: vs[0], maior: vs[vs.length - 1], media, mediana, cv, ref };
  })();

  const salvarIndexed = async () => {
    if (selectedIndexed.size === 0) { toast.error("Selecione ao menos 1 item"); return; }
    setSaving(true);
    try {
      const itens = [...selectedIndexed].map((i) => {
        const it = indexedItems[i];
        return {
          fonte: it.fonte || "PNCP", numero: it.numero_contrato || "", objeto: it.objeto_compra,
          fornecedor: it.fornecedor || "", valor: it.valor_unitario || 0,
          data: it.data_publicacao, orgao: it.orgao_nome, modalidade: it.modalidade,
          descricao_item: it.descricao, unidade_medida: it.unidade_medida,
          quantidade: it.quantidade, valor_total_item: it.valor_total,
          numero_item: it.numero_item, numero_controle_compra: it.numero_controle_compra,
          item_unico: false,
        };
      });
      const { data } = await api.post(`/contracts/${contractId}/pesquisa-precos`, {
        ...form, itens,
        contract_item_numero: itemContext?.numero_item ?? null,
      });
      setResult(data);
      setHistory((prev) => [data, ...prev]);
      setPhase("form");
      setIndexedItems([]);
      setSelectedIndexed(new Set());
      toast.success(`Pesquisa salva com ${data.itens.length} itens do banco indexado`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const fetchItens = async (candidato) => {
    const key = candidato.key;
    setItemsCache((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
    try {
      const { data } = await api.post(`/pesquisa-precos/fetch-itens`, {
        fonte: candidato.fonte,
        numero_controle_compra: candidato.numero_controle_compra || null,
      });
      setItemsCache((prev) => ({
        ...prev,
        [key]: { items: data.itens || [], semItens: !!data.sem_itens,
                 motivo: data.motivo || "", loaded: true, loading: false },
      }));
    } catch {
      setItemsCache((prev) => ({
        ...prev,
        [key]: { items: [], semItens: true, motivo: "Erro ao buscar itens",
                 loaded: true, loading: false },
      }));
    }
  };

  const toggleExpand = (candidato) => {
    const key = candidato.key;
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else {
        next.add(key);
        if (!itemsCache[key]?.loaded && !itemsCache[key]?.loading) fetchItens(candidato);
      }
      return next;
    });
  };

  const toggleKey = (itemKey) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey); else next.add(itemKey);
      return next;
    });
  };

  // Coleta todos itens-keys atualmente visíveis/relevantes
  const allItemKeys = () => {
    const keys = [];
    (preview?.candidatos || []).forEach((c) => {
      const cache = itemsCache[c.key];
      if (cache?.loaded && cache.items.length > 0) {
        cache.items.forEach((it) => keys.push(`${c.key}#item-${it.numero_item}`));
      } else {
        keys.push(`${c.key}#unico`);
      }
    });
    return keys;
  };

  const marcarTodos = () => setSelectedKeys(new Set(allItemKeys()));
  const desmarcarTodos = () => setSelectedKeys(new Set());
  const marcarNaoOutliers = () => setSelectedKeys(new Set(
    (preview?.candidatos || []).filter((c) => !c.is_outlier).flatMap((c) => {
      const cache = itemsCache[c.key];
      if (cache?.loaded && cache.items.length > 0) {
        return cache.items.filter((it) => (it.valor_unitario || 0) > 0)
                          .map((it) => `${c.key}#item-${it.numero_item}`);
      }
      return [`${c.key}#unico`];
    })
  ));

  // Resolve item key → valor de referência para stats
  const resolveItemValue = (itemKey) => {
    const [contractKey, suffix] = itemKey.split("#");
    const cand = (preview?.candidatos || []).find((c) => c.key === contractKey);
    if (!cand) return 0;
    if (suffix === "unico") return cand.valor || 0;
    const numItem = parseInt(suffix.replace("item-", ""), 10);
    const item = itemsCache[contractKey]?.items?.find((it) => it.numero_item === numItem);
    return item?.valor_unitario || 0;
  };

  // Stats live calculados localmente a partir da seleção
  const statsLive = (() => {
    const vs = [...selectedKeys].map(resolveItemValue).filter((v) => v > 0).sort((a, b) => a - b);
    if (!vs.length) return { qtd: 0, menor: 0, maior: 0, media: 0, mediana: 0, cv: 0, ref: 0, metodo: "—" };
    const media = vs.reduce((a, b) => a + b, 0) / vs.length;
    const mediana = vs.length % 2 === 0 ? (vs[vs.length / 2 - 1] + vs[vs.length / 2]) / 2 : vs[Math.floor(vs.length / 2)];
    const variance = vs.reduce((a, b) => a + (b - media) ** 2, 0) / (vs.length || 1);
    const desv = Math.sqrt(variance);
    const cv = media > 0 ? (desv / media) * 100 : 0;
    const metodo = cv <= 25 ? "media" : "menor_preco";
    const ref = metodo === "media" ? media : vs[0];
    return { qtd: vs.length, menor: vs[0], maior: vs[vs.length - 1], media, mediana, cv, ref, metodo };
  })();

  const salvarSelecao = async () => {
    if (selectedKeys.size === 0) { toast.error("Selecione ao menos 1 preço para salvar"); return; }
    setSaving(true);
    try {
      const itens = [...selectedKeys].map((itemKey) => {
        const [contractKey, suffix] = itemKey.split("#");
        const c = (preview?.candidatos || []).find((cd) => cd.key === contractKey);
        if (!c) return null;
        if (suffix === "unico") {
          return {
            fonte: c.fonte, numero: c.numero, objeto: c.objeto, fornecedor: c.fornecedor,
            valor: c.valor || 0, data: c.data, orgao: c.orgao, modalidade: c.modalidade,
            numero_controle_compra: c.numero_controle_compra || null, item_unico: true,
          };
        }
        const numItem = parseInt(suffix.replace("item-", ""), 10);
        const it = itemsCache[contractKey]?.items?.find((x) => x.numero_item === numItem);
        if (!it) return null;
        return {
          fonte: c.fonte, numero: c.numero, objeto: c.objeto, fornecedor: c.fornecedor,
          valor: it.valor_unitario || 0, data: c.data, orgao: c.orgao, modalidade: c.modalidade,
          descricao_item: it.descricao, unidade_medida: it.unidade_medida,
          quantidade: it.quantidade, valor_total_item: it.valor_total,
          numero_item: it.numero_item, numero_controle_compra: c.numero_controle_compra || null,
          item_unico: false,
        };
      }).filter(Boolean);

      const { data } = await api.post(`/contracts/${contractId}/pesquisa-precos`, {
        ...form,
        itens,
        contract_item_numero: itemContext?.numero_item ?? null,
      });
      setResult(data);
      setHistory((prev) => [data, ...prev]);
      setPhase("form");
      setPreview(null);
      toast.success(`Pesquisa salva com ${data.itens.length} itens curados`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const baixarPdf = async (pesqId) => {
    try {
      const res = await fetch(`${API}/pesquisas-precos/${pesqId}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pesquisa_precos_${pesqId.slice(0, 8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado");
    } catch { toast.error("Erro ao baixar PDF"); }
  };

  const excluir = async (pesqId) => {
    if (!window.confirm("Excluir esta pesquisa?")) return;
    try {
      await api.delete(`/pesquisas-precos/${pesqId}`);
      setHistory((prev) => prev.filter((p) => p.id !== pesqId));
      if (result?.id === pesqId) setResult(null);
      toast.success("Pesquisa excluída");
    } catch { toast.error("Erro ao excluir"); }
  };

  const view = result || history[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="pesquisa-precos-modal">
      <div className="tce-card w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#334155]">
          <div>
            <div className="uppercase-label">Conformidade · IN SEGES/ME nº 65/2021</div>
            <div className="font-heading text-xl mt-1">Pesquisa de Preços — Contrato {contract.numero}</div>
            {itemContext && (
              <div className="mt-2 px-3 py-1.5 rounded-md bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-xs text-[#FCD34D] inline-flex items-center gap-2">
                <span className="font-mono">Item #{itemContext.numero_item}</span>
                <span className="text-[#94A3B8]">·</span>
                <span className="truncate max-w-md">{itemContext.descricao}</span>
                <span className="text-[#94A3B8]">·</span>
                <span className="font-mono text-[#10B981]">{fmtBRL(itemContext.valor_unitario)}/un cadastrado</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-[#94A3B8] hover:text-[#E9EEF5]">✕</button>
        </div>

        {phase === "form" && (
          <form onSubmit={submit} className="p-6 border-b border-[#334155] grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-6 flex items-center gap-1 bg-[#0E141C]/60 p-1 rounded-md w-fit">
              <button
                type="button"
                data-testid="modo-contratos"
                onClick={() => setModo("contratos")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${modo === "contratos" ? "bg-[#F59E0B] text-[#0F172A]" : "text-[#94A3B8] hover:text-[#E9EEF5]"}`}
              >
                Por contratos (PNCP + TCE-CE)
              </button>
              <button
                type="button"
                data-testid="modo-indexados"
                onClick={() => setModo("indexados")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5 ${modo === "indexados" ? "bg-[#F59E0B] text-[#0F172A]" : "text-[#94A3B8] hover:text-[#E9EEF5]"}`}
              >
                <ChartBar size={12} weight="bold" />
                Banco indexado de itens (instantâneo)
              </button>
            </div>
            <div className="md:col-span-3">
              <label className="uppercase-label block mb-2">Termo de busca</label>
              <input data-testid="pesquisa-termo" required value={form.termo}
                     onChange={(e) => setForm({ ...form, termo: e.target.value })}
                     placeholder={modo === "indexados" ? "Ex: vassoura, papel A4, álcool 70" : "Ex: limpeza conservação, locação veículo"}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
            </div>
            {modo === "contratos" && (
              <>
                <div>
                  <label className="uppercase-label block mb-2">De</label>
                  <input data-testid="pesquisa-di" type="date" value={form.data_inicio}
                         onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                         className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
                </div>
                <div>
                  <label className="uppercase-label block mb-2">Até</label>
                  <input data-testid="pesquisa-df" type="date" value={form.data_fim}
                         onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                         className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
                </div>
              </>
            )}
            <button data-testid="pesquisa-submit" disabled={searching}
                    className={`px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 ${modo === "indexados" ? "md:col-start-6" : ""}`}>
              <MagnifyingGlass size={14} weight="bold" />
              {searching ? "Buscando…" : modo === "indexados" ? "Buscar itens" : "Buscar candidatos"}
            </button>
            {modo === "contratos" && (
              <div className="md:col-span-6 flex items-center gap-4 text-xs text-[#94A3B8]">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.incluir_pncp}
                         onChange={(e) => setForm({ ...form, incluir_pncp: e.target.checked })} />
                  <span>Incluir <strong>PNCP</strong> (Portal Nacional)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.incluir_tce}
                         onChange={(e) => setForm({ ...form, incluir_tce: e.target.checked })} />
                  <span>Incluir <strong>TCE-CE</strong> (base local 283k contratos)</span>
                </label>
                <span className="text-[10px] italic ml-auto">Você revisará e selecionará os preços antes de salvar</span>
              </div>
            )}
            {modo === "indexados" && (
              <div className="md:col-span-6 text-[11px] text-[#94A3B8] italic">
                Busca instantânea em itens PNCP previamente indexados (apenas UF=CE). Admins podem sincronizar novos períodos na página de Manutenção.
              </div>
            )}
          </form>
        )}

        {phase === "preview" && preview && (
          <PesquisaCuradoria
            preview={preview}
            selectedKeys={selectedKeys}
            statsLive={statsLive}
            expandedKeys={expandedKeys}
            itemsCache={itemsCache}
            termo={form.termo}
            onToggle={toggleKey}
            onExpand={toggleExpand}
            onAll={marcarTodos}
            onNone={desmarcarTodos}
            onNonOutliers={marcarNaoOutliers}
            onBack={() => { setPhase("form"); setPreview(null); }}
            onSave={salvarSelecao}
            saving={saving}
          />
        )}

        {phase === "indexed" && (
          <PesquisaItensIndexados
            items={indexedItems}
            selected={selectedIndexed}
            stats={statsLiveIndexed}
            termo={form.termo}
            onToggle={toggleIndexed}
            onAll={() => setSelectedIndexed(new Set(indexedItems.map((_, i) => i)))}
            onNone={() => setSelectedIndexed(new Set())}
            onBack={() => { setPhase("form"); setIndexedItems([]); setSelectedIndexed(new Set()); }}
            onSave={salvarIndexed}
            saving={saving}
          />
        )}

        {phase === "form" && (
          <div className="overflow-y-auto flex-1 p-6">
            {view ? (
              <PesquisaResultado pesq={view} onBaixar={() => baixarPdf(view.id)} onExcluir={() => excluir(view.id)} />
            ) : (
              <div className="text-center text-[#94A3B8] py-10 text-sm">
                Nenhuma pesquisa realizada ainda. Use o formulário acima para iniciar.
              </div>
            )}

            {history.length > 1 && (
              <div className="mt-6 pt-6 border-t border-[#334155]">
                <div className="uppercase-label mb-3">Histórico de pesquisas ({history.length})</div>
                <div className="space-y-2">
                  {history.slice(1).map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 rounded bg-[#1E293B]/50 text-sm">
                      <div>
                        <span className="text-[#F59E0B] font-mono mr-2">{p.termo}</span>
                        <span className="text-xs text-[#94A3B8]">
                          {p.estatisticas.qtd} itens · Ref: R$ {(p.estatisticas.preco_referencia || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => baixarPdf(p.id)} className="text-xs text-[#F59E0B] hover:text-[#FCD34D]">PDF</button>
                        <button onClick={() => excluir(p.id)} className="text-xs text-[#94A3B8] hover:text-[#EF4444]">Excluir</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PesquisaCuradoria({ preview, selectedKeys, statsLive, expandedKeys, itemsCache, termo, onToggle, onExpand, onAll, onNone, onNonOutliers, onBack, onSave, saving }) {
  const candidatos = preview.candidatos || [];
  const total = candidatos.length;
  const outlierCount = candidatos.filter((c) => c.is_outlier).length;
  const cvOk = statsLive.cv <= 25;
  const termoLow = (termo || "").toLowerCase();
  const matchTermo = (txt) => {
    if (!termoLow || !txt) return false;
    const t = txt.toLowerCase();
    return termoLow.split(" ").filter((w) => w.length > 2).every((w) => t.includes(w));
  };

  return (
    <div className="overflow-y-auto flex-1 flex flex-col" data-testid="pesquisa-curadoria">
      {/* Barra de stats live */}
      <div className="p-5 border-b border-[#334155] bg-[#1E293B]/30">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="uppercase-label !text-[#F59E0B]">Etapa 2 · Selecione preços por contrato ou por item</div>
            <div className="text-xs text-[#94A3B8] mt-1">
              {total} contrato(s) · {outlierCount} outlier(s) · termo: <span className="text-[#FCD34D]">{preview.termo}</span> ·
              <span className="ml-1 text-[#94A3B8]">clique no <strong>▸</strong> para expandir itens (PNCP)</span>
            </div>
          </div>
          <button data-testid="curadoria-voltar" onClick={onBack}
                  className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-xs flex items-center gap-1.5">
            <ArrowsClockwise size={12} /> Nova busca
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <Stat label="Selecionados" value={`${selectedKeys.size}`} hint="preços" />
          <Stat label="Menor" value={fmtBRL(statsLive.menor)} />
          <Stat label="Mediana" value={fmtBRL(statsLive.mediana)} />
          <Stat label="Média" value={fmtBRL(statsLive.media)} />
          <Stat label="CV" value={`${statsLive.cv.toFixed(1)}%`} hint={cvOk ? "OK (Art. 7º)" : ">25% — menor preço"} />
          <Stat label="Preço referência" value={fmtBRL(statsLive.ref)} accent />
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button data-testid="curadoria-all" onClick={onAll}
                  className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-xs flex items-center gap-1.5">
            <CheckSquare size={12} /> Marcar todos
          </button>
          <button data-testid="curadoria-none" onClick={onNone}
                  className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-xs flex items-center gap-1.5">
            <Square size={12} /> Desmarcar todos
          </button>
          <button data-testid="curadoria-no-outliers" onClick={onNonOutliers}
                  className="px-3 py-1.5 rounded-md border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10 text-xs flex items-center gap-1.5">
            <Scales size={12} weight="duotone" /> Apenas não-outliers (sugestão IN 65)
          </button>
          <div className="ml-auto">
            <button data-testid="curadoria-save" onClick={onSave} disabled={saving || selectedKeys.size === 0}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 disabled:opacity-40">
              <FilePdf size={14} weight="bold" />
              {saving ? "Salvando…" : `Salvar pesquisa (${selectedKeys.size} preços)`}
            </button>
          </div>
        </div>
      </div>

      {/* Lista de contratos expansíveis */}
      <div className="flex-1 overflow-y-auto">
        {candidatos.length === 0 && (
          <div className="text-center text-[#94A3B8] py-10 text-sm">
            Nenhum contrato encontrado. Refine o termo de busca.
          </div>
        )}
        {candidatos.map((c) => {
          const expanded = expandedKeys.has(c.key);
          const cache = itemsCache[c.key] || {};
          const itens = cache.items || [];
          const semItens = cache.semItens;
          const loading = cache.loading;
          const loaded = cache.loaded;
          const unicoKey = `${c.key}#unico`;
          const unicoChecked = selectedKeys.has(unicoKey);
          const showUnico = !loaded || semItens || itens.length === 0;
          // contagem de itens selecionados deste contrato
          const itensSelecionados = itens.filter((it) => selectedKeys.has(`${c.key}#item-${it.numero_item}`)).length;

          return (
            <div key={c.key} data-testid={`contrato-card-${c.key}`}
                 className={`border-b border-[#334155] ${c.is_outlier ? "bg-[#F59E0B]/[0.03]" : ""}`}>
              {/* Linha do contrato (expansível) */}
              <div
                onClick={() => onExpand(c)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#1E293B]/50 transition-colors"
              >
                <div className="text-[#F59E0B] w-4 shrink-0">
                  {expanded ? "▾" : "▸"}
                </div>
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#F59E0B]/15 text-[#F59E0B] shrink-0">{c.fonte}</span>
                <div className="font-mono text-xs whitespace-nowrap shrink-0">
                  <div className="text-[#E9EEF5]">{c.numero || "—"}</div>
                  <div className="text-[9px] text-[#94A3B8]">{c.data || "—"}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#E9EEF5] truncate" title={c.objeto}>{c.objeto}</div>
                  <div className="text-[10px] text-[#94A3B8] truncate">{c.fornecedor || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-xs text-[#FCD34D]">{fmtBRL(c.valor)}</div>
                  <div className="text-[9px] text-[#94A3B8]">
                    {loaded ? (semItens ? "valor global" : `${itens.length} itens · ${itensSelecionados} sel.`)
                            : (loading ? "buscando itens…" : "clique p/ expandir")}
                  </div>
                </div>
                <div className="shrink-0">
                  {c.is_outlier ? (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-[#F59E0B]/20 text-[#F59E0B]">outlier</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-[#10B981]/20 text-[#10B981]">ok</span>
                  )}
                </div>
              </div>

              {/* Itens expandidos */}
              {expanded && (
                <div className="bg-[#0E141C]/40 border-t border-[#334155] px-6 py-3">
                  {loading && <div className="text-xs text-[#94A3B8] py-2">Buscando itens no PNCP…</div>}

                  {loaded && showUnico && (
                    <div
                      data-testid={`item-unico-${c.key}`}
                      onClick={() => onToggle(unicoKey)}
                      className={`flex items-center gap-3 p-2.5 rounded cursor-pointer transition-colors ${unicoChecked ? "bg-[#F59E0B]/10" : "hover:bg-[#1E293B]/50"}`}
                    >
                      <input type="checkbox" checked={unicoChecked}
                             onChange={() => onToggle(unicoKey)}
                             onClick={(e) => e.stopPropagation()}
                             className="accent-[#F59E0B]" />
                      <Warning size={12} className="text-[#F59E0B]" />
                      <div className="flex-1 text-xs">
                        <div className="text-[#E9EEF5]">Item único · valor global do contrato</div>
                        <div className="text-[10px] text-[#94A3B8]">{cache.motivo || "Sem itens detalhados disponíveis"}</div>
                      </div>
                      <div className="text-xs font-mono text-[#FCD34D]">{fmtBRL(c.valor)}</div>
                    </div>
                  )}

                  {loaded && !semItens && itens.length > 0 && (
                    <div className="space-y-1">
                      {itens.map((it) => {
                        const itemKey = `${c.key}#item-${it.numero_item}`;
                        const isChecked = selectedKeys.has(itemKey);
                        const isMatch = matchTermo(it.descricao);
                        const semValor = !it.valor_unitario || it.valor_unitario <= 0;
                        return (
                          <div
                            key={itemKey}
                            data-testid={`item-row-${itemKey}`}
                            onClick={() => !semValor && onToggle(itemKey)}
                            className={`flex items-center gap-3 p-2 rounded text-xs transition-colors ${semValor ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${isChecked ? "bg-[#F59E0B]/10" : isMatch ? "bg-[#10B981]/5 hover:bg-[#10B981]/10" : "hover:bg-[#1E293B]/50"}`}
                          >
                            <input type="checkbox" checked={isChecked} disabled={semValor}
                                   onChange={() => !semValor && onToggle(itemKey)}
                                   onClick={(e) => e.stopPropagation()}
                                   className="accent-[#F59E0B]" />
                            <div className="font-mono text-[10px] text-[#94A3B8] shrink-0 w-8 text-right">#{it.numero_item}</div>
                            <div className="flex-1 min-w-0">
                              <div className={`truncate ${isMatch ? "text-[#10B981] font-semibold" : "text-[#E9EEF5]"}`} title={it.descricao}>
                                {it.descricao}
                              </div>
                              <div className="text-[10px] text-[#94A3B8]">
                                {it.material_servico || "—"} · {it.quantidade > 0 ? `${it.quantidade} ${it.unidade_medida || "un"}` : "—"}
                                {it.sigiloso && <span className="ml-2 text-[#F59E0B]">⚠ sigiloso</span>}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-mono text-[#FCD34D]">{fmtBRL(it.valor_unitario)}/un</div>
                              {it.valor_total > 0 && (
                                <div className="text-[9px] text-[#94A3B8]">total {fmtBRL(it.valor_total)}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PesquisaResultado({ pesq, onBaixar, onExcluir }) {
  const st = pesq.estatisticas || {};
  const metodoLabel = st.metodo_sugerido === "media" ? "Média aritmética" : "Menor preço (CV > 25%)";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Itens válidos" value={st.qtd} hint={`${st.outliers_descartados} outliers descartados`} />
        <Stat label="CV (variação)" value={`${(st.cv || 0).toFixed(2)}%`} hint={(st.cv || 0) <= 25 ? "Dentro do limite (Art. 7º)" : "Alto — método: menor preço"} />
        <Stat label="Método aplicado" value={metodoLabel} hint="Art. 6º da IN 65/2021" />
        <Stat label="Preço de referência" value={fmtBRL(st.preco_referencia)} accent />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-[#94A3B8]">
        <div>Menor: <span className="font-mono text-[#10B981]">{fmtBRL(st.menor)}</span></div>
        <div>Média: <span className="font-mono text-[#E9EEF5]">{fmtBRL(st.media)}</span></div>
        <div>Mediana: <span className="font-mono text-[#E9EEF5]">{fmtBRL(st.mediana)}</span></div>
        <div>Maior: <span className="font-mono text-[#F59E0B]">{fmtBRL(st.maior)}</span></div>
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="pesquisa-baixar-pdf" onClick={onBaixar}
                className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-sm font-medium flex items-center gap-2">
          <FilePdf size={14} weight="bold" /> Baixar PDF (relatório oficial)
        </button>
        <button onClick={onExcluir}
                className="px-4 py-2 rounded-md border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 text-sm">
          Excluir esta pesquisa
        </button>
      </div>
      <div className="border border-[#334155] rounded-md overflow-hidden">
        <div className="px-4 py-2 bg-[#1E293B] uppercase-label">
          {pesq.itens.length} contratos comparáveis · {pesq.fontes.join(" + ")}
        </div>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#1E293B]/50 sticky top-0">
              <tr><th className="px-3 py-2 text-left">Fonte</th><th className="px-3 py-2 text-left">Nº</th>
                  <th className="px-3 py-2 text-left">Objeto</th><th className="px-3 py-2 text-left">Fornecedor</th>
                  <th className="px-3 py-2 text-right">Valor</th><th className="px-3 py-2 text-left">Data</th></tr>
            </thead>
            <tbody>
              {pesq.itens.slice(0, 50).map((it, i) => (
                <tr key={`${it.fonte}-${i}-${it.numero}`} className="border-t border-[#334155]">
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded text-[10px] bg-[#F59E0B]/15 text-[#F59E0B]">{it.fonte}</span></td>
                  <td className="px-3 py-2 font-mono">{it.numero}</td>
                  <td className="px-3 py-2 max-w-[300px] truncate" title={it.objeto}>{it.objeto}</td>
                  <td className="px-3 py-2 max-w-[180px] truncate">{it.fornecedor}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#FCD34D]">{fmtBRL(it.valor)}</td>
                  <td className="px-3 py-2 text-[#94A3B8]">{it.data || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, accent }) {
  return (
    <div className="p-3 rounded-md border border-[#334155] bg-[#0E141C]/40">
      <div className="uppercase-label !text-[10px]">{label}</div>
      <div className={`font-mono text-lg mt-1 ${accent ? "gold-text" : "text-[#E9EEF5]"}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#94A3B8] mt-1">{hint}</div>}
    </div>
  );
}


function InfoCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="tce-card p-5">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-9 h-9 rounded-md bg-[#1E293B] flex items-center justify-center text-[#F59E0B] flex-shrink-0">
            <Icon size={18} weight="duotone" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="uppercase-label">{label}</div>
          <div className={`mt-1.5 ${accent ? "font-heading text-2xl gold-text" : "text-[#E9EEF5]"}`}>{value}</div>
        </div>
      </div>
    </div>
  );
}

function _saldoLevel(pct) {
  if (pct >= 100) return { color: "#EF4444", label: "Saldo ESGOTADO" };
  if (pct >= 80) return { color: "#EF4444", label: "Saldo CRÍTICO" };
  if (pct >= 60) return { color: "#F59E0B", label: "Saldo em alerta" };
  return { color: "#10B981", label: "OK" };
}

function ExecucaoForm({ execForm, setExecForm, contractItens, contractMunicipioId, execItens, setExecItens, onSubmit, saving }) {
  const itensDisponiveis = (contractItens || []).filter((it) => {
    const saldo = (parseFloat(it.quantidade) || 0) - (parseFloat(it.quantidade_executada) || 0);
    return saldo > 0;
  });
  const addItem = () => setExecItens([...(execItens || []), { numero_item: "", quantidade: "", valor_unitario: "" }]);
  const updItem = (idx, patch) => setExecItens(execItens.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const rmItem = (idx) => setExecItens(execItens.filter((_, i) => i !== idx));
  const somaItens = (execItens || []).reduce((s, it) => s + (parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0), 0);

  return (
    <form onSubmit={onSubmit} className="mt-6 pt-6 border-t border-[#334155] grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
      <div className="md:col-span-1">
        <label className="uppercase-label block mb-2">Valor (R$)</label>
        <input data-testid="exec-valor" type="number" step="0.01" required
               value={execForm.valor}
               onChange={(e) => setExecForm({ ...execForm, valor: e.target.value })}
               className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono" />
      </div>
      <div className="md:col-span-1">
        <label className="uppercase-label block mb-2">Tipo</label>
        <select data-testid="exec-tipo" value={execForm.tipo}
                onChange={(e) => setExecForm({ ...execForm, tipo: e.target.value })}
                className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
          <option value="empenho">Empenho</option>
          <option value="liquidacao">Liquidação</option>
          <option value="pagamento">Pagamento</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="uppercase-label block mb-2">Nº Nota Fiscal</label>
        <input data-testid="exec-nf" value={execForm.numero_nota_fiscal}
               onChange={(e) => setExecForm({ ...execForm, numero_nota_fiscal: e.target.value })}
               placeholder="Ex: NF-2026-000123"
               className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
      </div>
      <div className="md:col-span-2">
        <label className="uppercase-label block mb-2">Data emissão NF</label>
        <input data-testid="exec-nf-data" type="date" value={execForm.data_emissao_nf}
               onChange={(e) => setExecForm({ ...execForm, data_emissao_nf: e.target.value })}
               className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
      </div>
      <div className="md:col-span-6">
        <label className="uppercase-label block mb-2">Descrição (opcional)</label>
        <input data-testid="exec-desc" value={execForm.descricao}
               onChange={(e) => setExecForm({ ...execForm, descricao: e.target.value })}
               placeholder="Detalhes do empenho/pagamento, parcela, observações…"
               className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
      </div>

      {/* Itens debitados */}
      {itensDisponiveis.length > 0 && (
        <div className="md:col-span-6 bg-[#0B1220] border border-[#334155] rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="uppercase-label !text-[#F59E0B]">Itens debitados do saldo do contrato (opcional)</div>
              <div className="text-[10px] text-[#94A3B8] mt-1">
                Selecione quais itens/quantidades estão sendo consumidos por esta NF. O saldo do item será decrementado automaticamente.
              </div>
            </div>
            <button type="button" onClick={addItem} data-testid="exec-add-item"
                    className="px-2.5 py-1.5 rounded border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs">
              + Adicionar item
            </button>
          </div>
          {(execItens || []).map((it, idx) => {
            const itemContrato = itensDisponiveis.find((x) => x.numero_item === parseInt(it.numero_item, 10));
            const saldoQtd = itemContrato
              ? (parseFloat(itemContrato.quantidade) || 0) - (parseFloat(itemContrato.quantidade_executada) || 0)
              : 0;
            const excede = parseFloat(it.quantidade) > saldoQtd;
            return (
              <div key={idx} data-testid={`exec-item-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-center">
                <select value={it.numero_item} required
                        onChange={(e) => {
                          const sel = itensDisponiveis.find((x) => x.numero_item === parseInt(e.target.value, 10));
                          updItem(idx, { numero_item: e.target.value, valor_unitario: sel?.valor_unitario || "" });
                        }}
                        className="tce-input col-span-6 px-2 py-1.5 rounded text-xs">
                  <option value="">Escolha item...</option>
                  {itensDisponiveis.map((x) => {
                    const s = (parseFloat(x.quantidade) || 0) - (parseFloat(x.quantidade_executada) || 0);
                    return <option key={x.numero_item} value={x.numero_item}>
                      Item {x.numero_item} — {(x.descricao || "").slice(0, 30)} (saldo: {s} {x.unidade_medida || "UN"})
                    </option>;
                  })}
                </select>
                <input type="number" step="0.01" min="0.01" max={saldoQtd || undefined} placeholder="Qtd" required
                       value={it.quantidade}
                       onChange={(e) => updItem(idx, { quantidade: e.target.value })}
                       className={`tce-input col-span-2 px-2 py-1.5 rounded text-xs ${excede ? "border-[#EF4444]" : ""}`} />
                <input type="number" step="0.01" placeholder="Vl. unit."
                       value={it.valor_unitario}
                       onChange={(e) => updItem(idx, { valor_unitario: e.target.value })}
                       className="tce-input col-span-2 px-2 py-1.5 rounded text-xs" />
                <div className="col-span-1 text-xs text-[#94A3B8] font-mono">
                  {((parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0)).toFixed(2)}
                </div>
                <button type="button" onClick={() => rmItem(idx)}
                        className="col-span-1 text-[#EF4444] hover:text-[#FCA5A5] text-xs">×</button>
              </div>
            );
          })}
          {(execItens || []).length > 0 && (
            <div className="text-[11px] text-[#FCD34D] mt-2 font-mono">
              Soma dos itens: R$ {somaItens.toFixed(2)} — {Math.abs(somaItens - (parseFloat(execForm.valor) || 0)) < 0.01
                ? "✓ bate com o valor da NF"
                : `⚠ divergente do valor da NF (R$ ${(parseFloat(execForm.valor) || 0).toFixed(2)})`}
            </div>
          )}
        </div>
      )}

      <div className="md:col-span-6 bg-[#0B1220] border border-[#334155] rounded-md p-4">
        <SignatarioPicker
          municipioId={contractMunicipioId}
          value={execForm.signatario_id}
          onChange={(v) => setExecForm({ ...execForm, signatario_id: v })}
          testid="exec-signatario"
          required
        />
      </div>

      <button data-testid="exec-submit" disabled={saving || !execForm.signatario_id}
              className="md:col-span-6 px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50">
        {saving ? "Registrando…" : "Confirmar execução"}
      </button>
    </form>
  );
}

async function downloadExecucaoReport(contractId, exec) {
  try {
    const res = await fetch(`${API}/contracts/${contractId}/execucoes/${exec.id}/report`, { credentials: "include" });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio_NF_${(exec.numero_nota_fiscal || exec.id.slice(0, 8)).replace(/[^\w.-]/g, "_")}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Relatório da NF ${exec.numero_nota_fiscal || "sem número"} gerado`);
  } catch {
    toast.error("Erro ao gerar relatório da NF");
  }
}

function ExecucoesHistorico({ execucoes, contractId, contractMunicipioId, onDeleted }) {
  const navigate = useNavigate();
  const [downloadEx, setDownloadEx] = useState(null);
  if (!execucoes || execucoes.length === 0) return null;
  const excluir = async (ex) => {
    const label = ex.numero_nota_fiscal ? `NF ${ex.numero_nota_fiscal}` : `${ex.tipo} de ${fmtBRL(ex.valor)}`;
    if (!window.confirm(`Excluir o registro (${label})?\n\nO saldo do contrato será revertido: ${fmtBRL(ex.valor)} voltarão ao saldo disponível e as quantidades dos itens debitados serão restauradas.`)) return;
    try {
      await api.delete(`/contracts/${contractId}/execucoes/${ex.id}`);
      toast.success("Registro excluído e saldo revertido");
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir registro");
    }
  };
  return (
    <div className="mt-6 pt-6 border-t border-[#334155]">
      <div className="uppercase-label mb-3">Histórico de execuções ({execucoes.length})</div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {execucoes.map((ex, i) => (
          <div key={ex.id} data-testid={`exec-row-${i}`} className="flex items-center justify-between text-sm p-2.5 rounded bg-[#1E293B]/50">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="text-xs px-2 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] uppercase">{ex.tipo}</span>
              {ex.nao_abate_saldo && <span data-testid={`exec-nao-abate-${i}`} className="text-[10px] px-2 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]">não abate saldo</span>}
              {ex.medicao_id && <span className="text-[10px] px-2 py-0.5 rounded bg-[#3B82F6]/15 text-[#3B82F6]">medição</span>}
              <div className="min-w-0 flex-1">
                <div className="text-[#E9EEF5] truncate">
                  {ex.numero_nota_fiscal && (
                    <span className="font-mono text-[#FCD34D] mr-2">NF {ex.numero_nota_fiscal}</span>
                  )}
                  {ex.descricao || (ex.numero_nota_fiscal ? "" : "—")}
                </div>
                <div className="text-xs text-[#94A3B8]">
                  {ex.fiscal_name} · {new Date(ex.created_at).toLocaleString("pt-BR")}
                  {ex.data_emissao_nf && ` · Emissão NF: ${ex.data_emissao_nf}`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[#FCD34D]">{fmtBRL(ex.valor)}</span>
              <button
                data-testid={`exec-editor-${i}`}
                onClick={() => navigate(`/editor/execucao/${ex.id}`)}
                title="Abrir Editor Inteligente da atestação (revisão com IA)"
                className="p-1.5 rounded text-[#94A3B8] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
              >
                <PencilSimple size={14} weight="duotone" />
              </button>
              <button
                data-testid={`exec-pdf-${i}`}
                onClick={() => setDownloadEx(ex)}
                title="Gerar PDF desta nota fiscal"
                className="p-1.5 rounded text-[#94A3B8] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors"
              >
                <FilePdf size={14} weight="duotone" />
              </button>
              <button
                data-testid={`exec-delete-${i}`}
                onClick={() => excluir(ex)}
                title="Excluir registro e reverter saldo"
                className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
              >
                <Trash size={14} weight="duotone" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {downloadEx && (
        <SignatarioDownloadModal
          municipioId={contractMunicipioId}
          urlBase={`/contracts/${contractId}/execucoes/${downloadEx.id}/report`}
          filename={`relatorio_NF_${(downloadEx.numero_nota_fiscal || downloadEx.id.slice(0, 8)).replace(/[^\w.-]/g, "_")}.pdf`}
          title="Relatório de Nota Fiscal"
          subtitle={downloadEx.numero_nota_fiscal ? `NF ${downloadEx.numero_nota_fiscal}` : `Execução ${downloadEx.id.slice(0, 8)}`}
          onClose={() => setDownloadEx(null)}
        />
      )}
    </div>
  );
}

function SaldoPanel({ saldo, contractId, contractMunicipioId, contractItens, showForm, toggleForm, execForm, setExecForm, execItens, setExecItens, onSubmit, saving, onExecDeleted }) {
  if (!saldo) return null;
  const pct = saldo.pct_consumido || 0;
  const { color: barColor, label: level } = _saldoLevel(pct);

  return (
    <div className="tce-card p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B]">
            <Wallet size={22} weight="duotone" />
          </div>
          <div>
            <div className="uppercase-label">Controle financeiro</div>
            <div className="font-heading text-2xl mt-1">Saldo do Contrato</div>
          </div>
        </div>
        <button
          data-testid="add-execucao-button"
          onClick={toggleForm}
          className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-sm font-medium flex items-center gap-2"
        >
          <Plus size={14} weight="bold" /> Registrar execução
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <Metric label="Valor total" value={fmtBRL(saldo.valor_total)} />
        <Metric label="Executado" value={fmtBRL(saldo.valor_executado)} color="#F59E0B" />
        <Metric label="Saldo restante" value={fmtBRL(saldo.saldo)} color={saldo.saldo < 0 ? "#EF4444" : "#10B981"} />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="uppercase-label">% Consumido</span>
          <span className="font-mono font-bold" style={{ color: barColor }}>{pct.toFixed(1)}% · {level}</span>
        </div>
        <div className="h-3 bg-[#1E293B] rounded-full overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
        </div>
        {pct >= 80 && (
          <div className="flex items-start gap-2 mt-3 p-3 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/30 text-sm text-[#EF4444]">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
            <span>Atenção: este contrato está com saldo crítico. Avalie aditivos ou nova licitação.</span>
          </div>
        )}
      </div>

      {showForm && <ExecucaoForm execForm={execForm} setExecForm={setExecForm}
                                  contractItens={contractItens} contractMunicipioId={contractMunicipioId}
                                  execItens={execItens} setExecItens={setExecItens}
                                  onSubmit={onSubmit} saving={saving} />}
      <ExecucoesHistorico execucoes={saldo.execucoes} contractId={contractId} contractMunicipioId={contractMunicipioId} onDeleted={onExecDeleted} />
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="rounded-md bg-[#0F172A] border border-[#334155] p-4">
      <div className="uppercase-label">{label}</div>
      <div className="font-heading text-xl mt-2" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}



function PesquisaItensIndexados({ items, selected, stats, termo, onToggle, onAll, onNone, onBack, onSave, saving }) {
  const cvOk = stats.cv <= 25;
  const termoLow = (termo || "").toLowerCase();
  const matchTermo = (txt) => {
    if (!termoLow || !txt) return false;
    const t = txt.toLowerCase();
    return termoLow.split(" ").filter((w) => w.length > 2).every((w) => t.includes(w));
  };

  return (
    <div className="overflow-y-auto flex-1 flex flex-col" data-testid="pesquisa-indexados">
      <div className="p-5 border-b border-[#334155] bg-[#1E293B]/30">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="uppercase-label !text-[#F59E0B]">Banco indexado · resultados instantâneos</div>
            <div className="text-xs text-[#94A3B8] mt-1">
              {items.length} item(s) encontrado(s) · termo: <span className="text-[#FCD34D]">{termo}</span>
            </div>
          </div>
          <button data-testid="indexed-voltar" onClick={onBack}
                  className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-xs flex items-center gap-1.5">
            <ArrowsClockwise size={12} /> Nova busca
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <Stat label="Selecionados" value={`${selected.size}/${items.length}`} />
          <Stat label="Menor" value={fmtBRL(stats.menor)} />
          <Stat label="Mediana" value={fmtBRL(stats.mediana)} />
          <Stat label="Média" value={fmtBRL(stats.media)} />
          <Stat label="CV" value={`${stats.cv.toFixed(1)}%`} hint={cvOk ? "OK (Art. 7º)" : ">25% — menor preço"} />
          <Stat label="Preço referência" value={fmtBRL(stats.ref)} accent />
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button data-testid="indexed-all" onClick={onAll}
                  className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-xs flex items-center gap-1.5">
            <CheckSquare size={12} /> Marcar todos
          </button>
          <button data-testid="indexed-none" onClick={onNone}
                  className="px-3 py-1.5 rounded-md border border-[#334155] hover:border-[#F59E0B] text-xs flex items-center gap-1.5">
            <Square size={12} /> Desmarcar todos
          </button>
          <div className="ml-auto">
            <button data-testid="indexed-save" onClick={onSave} disabled={saving || selected.size === 0}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 disabled:opacity-40">
              <FilePdf size={14} weight="bold" />
              {saving ? "Salvando…" : `Salvar pesquisa (${selected.size} itens)`}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {items.length === 0 && (
          <div className="text-center text-[#94A3B8] py-10 text-sm">
            Nenhum item indexado encontrado para este termo. Admins podem sincronizar mais períodos em <strong>Manutenção</strong>.
          </div>
        )}
        <div className="divide-y divide-[#334155]">
          {items.map((it, idx) => {
            const isChecked = selected.has(idx);
            const isMatch = matchTermo(it.descricao);
            return (
              <div
                key={`${it.numero_controle_compra}-${it.numero_item}-${idx}`}
                data-testid={`indexed-row-${idx}`}
                onClick={() => onToggle(idx)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${isChecked ? "bg-[#F59E0B]/10" : isMatch ? "bg-[#10B981]/5 hover:bg-[#10B981]/10" : "hover:bg-[#1E293B]/50"}`}
              >
                <input type="checkbox" checked={isChecked}
                       onChange={() => onToggle(idx)}
                       onClick={(e) => e.stopPropagation()}
                       className="accent-[#F59E0B]" />
                <span className={`px-1.5 py-0.5 rounded text-[10px] shrink-0 ${it.fonte === "Comprasnet" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>{it.fonte || "PNCP"}</span>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs truncate ${isMatch ? "text-[#10B981] font-semibold" : "text-[#E9EEF5]"}`} title={it.descricao}>
                    {it.descricao}
                  </div>
                  <div className="text-[10px] text-[#94A3B8] truncate">
                    {it.municipio_nome} · {it.orgao_nome} · {it.data_publicacao}
                    {it.unidade_medida && <span> · {it.quantidade > 0 ? `${it.quantidade} ${it.unidade_medida}` : it.unidade_medida}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-xs text-[#FCD34D]">{fmtBRL(it.valor_unitario)}/un</div>
                  {it.valor_total > 0 && (
                    <div className="text-[9px] text-[#94A3B8]">total {fmtBRL(it.valor_total)}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
